import type { Cents } from "../domain/money";
import { formatCents } from "../domain/money";
import { formatX100 } from "../domain/measurement";
import {
  componentIncluded,
  type AssemblyInstance,
  type AssemblyInstanceComponent,
  type AssemblyOption,
} from "../domain/assembly";
import { effectiveFeatureQuantity } from "../domain/feature";

/**
 * Deterministic assembly calculation (Phase 4).
 *
 * Computes CONSTRUCTION REQUIREMENTS — never retailer package quantities
 * (product package sizing/ceil() is deliberately deferred to the future
 * Material Catalog / Procurement system).
 *
 * Rounding rules (pinned by tests):
 *  - quantity math stays in exact hundredths; each derivation step rounds
 *    half-up to the nearest hundredth;
 *  - money is rounded half-up to the cent at the subtotal multiplication;
 *  - estimating tax (if any) is rounded half-up to the cent;
 *  - the generated line amount is all-in: subtotal + estimated tax.
 */

/** n/d rounded half-up, for non-negative integers. */
function roundDiv(n: number, d: number): number {
  return Math.floor((n + d / 2) / d);
}

export interface DrivingQuantity {
  valueX100: number;
  unit: string;
  /** e.g. 'manual' — from the feature's quantity provenance. */
  source: string;
}

/**
 * Resolve the assembly's driving quantity from the feature (spec §21):
 * effective feature quantity (geometry / override / manual) must match the
 * assembly's driving unit; unbound 'each' assemblies default to 1 when nothing is
 * entered. A missing bound measurement stays unknown. Single implementation shared by regeneration and the BOM.
 */
export function resolveDrivingQuantity(
  feature: Parameters<typeof effectiveFeatureQuantity>[0],
  drivingUnit: string,
): { ok: true; driving: DrivingQuantity } | { ok: false; error: string } {
  const eff = effectiveFeatureQuantity(feature);
  if (eff.valueX100 === null) {
    if (feature.quantityGeometryBinding) return { ok: false, error: "The source measurement is missing. Restore its geometry or enter an explicit quantity override." };
    if (drivingUnit === "each") return { ok: true, driving: { valueX100: 100, unit: "each", source: "default (1 each)" } };
    return { ok: false, error: `This assembly needs the feature's quantity in ${drivingUnit}. Enter it on the feature first.` };
  }
  if (eff.unit !== drivingUnit) {
    return {
      ok: false,
      error: `This assembly expects ${drivingUnit}, but the feature quantity is in ${eff.unit}. Update the feature quantity (no automatic unit conversion).`,
    };
  }
  return { ok: true, driving: { valueX100: eff.valueX100, unit: eff.unit, source: eff.source } };
}

export interface ComponentCalculation {
  componentId: string;
  name: string;
  included: boolean;
  /** Human-readable reason ("required", "option 'X' = Yes", "optional, excluded"…). */
  inclusionReason: string;
  calculatedQtyX100: number;
  qtyOverrideX100: number | null;
  effectiveQtyX100: number;
  unit: string;
  unitCostCents: Cents;
  subtotalCents: Cents;
  estimatedTaxCents: Cents;
  /** All-in amount written to the generated Proposed Cost Line. */
  amountCents: Cents;
  /** Step-by-step explanation of the deterministic calculation. */
  explanation: string[];
}

export interface AssemblyCalculation {
  components: ComponentCalculation[];
  /** Σ amounts of included components. */
  totalCents: Cents;
}

function inclusionReason(
  component: AssemblyInstanceComponent,
  optionsById: Map<string, AssemblyOption>,
): string {
  if (component.archivedAt !== null) return "removed from this assembly";
  switch (component.inclusion) {
    case "required":
      return "required";
    case "conditional": {
      const option = component.conditionOptionId
        ? optionsById.get(component.conditionOptionId)
        : undefined;
      if (!option) return "conditional (no option — excluded)";
      return `option “${option.prompt}” = ${option.value ? "Yes" : "No"}`;
    }
    case "optional":
      return component.included ? "optional — included" : "optional — excluded";
  }
}

/** Company estimating labor rate default (spec §20A): $25/hour. */
export const DEFAULT_LABOR_RATE_CENTS_PER_HOUR = 2500;

const formatDuration = (minutesX100: number): string => {
  const totalMinutes = Math.floor(minutesX100 / 100);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return h > 0 ? `${h} hr ${m} min` : `${m} min`;
};

export function computeComponent(
  component: AssemblyInstanceComponent,
  optionsById: Map<string, AssemblyOption>,
  driving: DrivingQuantity,
  estimatingTaxPctX100: number | null,
  laborRateCentsPerHour: number = DEFAULT_LABOR_RATE_CENTS_PER_HOUR,
): ComponentCalculation {
  const included = componentIncluded(component, optionsById);
  const explanation: string[] = [];

  // ---- Quantity (construction requirement) ----
  let calculated: number;
  if (component.qtyBasis === "fixed") {
    calculated = component.fixedQtyX100;
    explanation.push(`Fixed quantity: ${formatX100(calculated)} ${component.unit}`);
  } else {
    explanation.push(
      `Feature quantity: ${formatX100(driving.valueX100)} ${driving.unit} (source: ${driving.source})`,
    );
    calculated = roundDiv(driving.valueX100 * component.multiplierX10000, 10000);
    if (component.multiplierX10000 !== 10000) {
      explanation.push(
        `× multiplier ${formatX100(roundDiv(component.multiplierX10000, 100))} = ${formatX100(calculated)} ${component.unit}`,
      );
    }
    if (component.wastePctX100 > 0) {
      calculated = roundDiv(calculated * (10000 + component.wastePctX100), 10000);
      explanation.push(
        `+ ${formatX100(component.wastePctX100)}% waste = ${formatX100(calculated)} ${component.unit} required`,
      );
    } else {
      explanation.push(`Required: ${formatX100(calculated)} ${component.unit}`);
    }
  }

  const effective = component.qtyOverrideX100 ?? calculated;
  if (component.qtyOverrideX100 !== null) {
    explanation.push(
      `User override: ${formatX100(component.qtyOverrideX100)} ${component.unit} (calculated ${formatX100(calculated)} preserved)`,
    );
  }

  // ---- Money ----
  let subtotal: number;
  if (component.componentType === "labor" && component.laborMinutesX100 !== null) {
    // Duration-based labor (spec §20A): total estimated duration, rounded
    // UP to whole hours, × the configured company estimating rate. Exact
    // integer math; fully explained — never an opaque "smart" total.
    const totalMinutesX100 = roundDiv(effective * component.laborMinutesX100, 100);
    const billableHours = Math.floor((totalMinutesX100 + 5999) / 6000);
    subtotal = billableHours * laborRateCentsPerHour;
    explanation.push(
      `Estimated duration: ${formatDuration(component.laborMinutesX100)} per ${component.unit} × ${formatX100(effective)} ${component.unit} = ${formatDuration(totalMinutesX100)}`,
    );
    explanation.push(`Billable estimating hours (rounded up): ${billableHours} hr`);
    explanation.push(
      `Company estimating rate: ${formatCents(laborRateCentsPerHour)}/hr (estimating assumption — not a contractor quote)`,
    );
    explanation.push(`${billableHours} hr × ${formatCents(laborRateCentsPerHour)}/hr = ${formatCents(subtotal)}`);
  } else {
    subtotal = roundDiv(effective * component.unitCostCents, 100);
    explanation.push(
      `${formatX100(effective)} ${component.unit} × ${formatCents(component.unitCostCents)}/${component.unit} = ${formatCents(subtotal)}`,
    );
  }

  let tax = 0;
  if (component.taxable && estimatingTaxPctX100 !== null && estimatingTaxPctX100 > 0) {
    tax = roundDiv(subtotal * estimatingTaxPctX100, 10000);
    explanation.push(
      `+ estimated sales tax ${formatX100(estimatingTaxPctX100)}% = ${formatCents(tax)} (all-in ${formatCents(subtotal + tax)})`,
    );
  }

  return {
    componentId: component.id,
    name: component.name,
    included,
    inclusionReason: inclusionReason(component, optionsById),
    calculatedQtyX100: calculated,
    qtyOverrideX100: component.qtyOverrideX100,
    effectiveQtyX100: effective,
    unit: component.unit,
    unitCostCents: component.unitCostCents,
    subtotalCents: subtotal,
    estimatedTaxCents: tax,
    amountCents: subtotal + tax,
    explanation,
  };
}

export function computeAssembly(
  instance: Pick<AssemblyInstance, "options" | "components">,
  driving: DrivingQuantity,
  estimatingTaxPctX100: number | null,
  laborRateCentsPerHour: number = DEFAULT_LABOR_RATE_CENTS_PER_HOUR,
): AssemblyCalculation {
  const optionsById = new Map(instance.options.map((o) => [o.id, o]));
  const components = instance.components
    .filter((c) => c.archivedAt === null)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((c) => computeComponent(c, optionsById, driving, estimatingTaxPctX100, laborRateCentsPerHour));
  const totalCents = components
    .filter((c) => c.included)
    .reduce((sum, c) => sum + c.amountCents, 0);
  return { components, totalCents };
}
